import { RefObject, useRef, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { pick } from 'lodash-es'
import { useForm } from 'react-hook-form'

import { SIGN_UP_CUSTOMER, LOGOUT_CUSTOMER, GET_CURRENT_SHOP_TWO_FACTOR_AUTH } from 'Const'
import { Customer } from 'Types'
import { sendGTMAccountCreatedEvent } from 'Utils/analytics/gtmEvents'
import { noty } from 'Utils/index'

import { SignUpStates, useSignUpState } from './checkout'
import useEvent from './useEvent'

export type SignedUpCustomer = Pick<Customer, 'firstName' | 'lastName' | 'email' | 'phone'>
type SignUpCustomerArg = Pick<Customer, 'signedUp' | 'firstName' | 'lastName' | 'email' | 'phone'>

export interface SignUpFormState {
  code?: string
  email: string
  firstName: string
  lastName: string
  password?: string
  phone: string
  subscribed?: boolean
  subscribedSms?: boolean
}

const RESEND_COOLDOWN_SECONDS = 60
const DEFAULT_FORM_VALUES: SignUpFormState = {
  code: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  phone: '',
  subscribed: true,
}

interface Args {
  /** Called right after logout completes — e.g. Checkout also steps back its own wizard here. */
  onLoggedOut?: () => void
  /** Called once sign-up reaches SignUpStates.COMPLETED, and immediately if `customer` is already signed up. */
  onSignedUp: (customer: SignedUpCustomer) => void
}

// The actual sign-up/verification state machine (phone -> code -> details/password
// -> completed) behind both Checkout's CustomerFieldsGroup and Profile's LoginForm.
// Pulled out so a fix here (see the Code-mounting comment in each component's JSX)
// only has to happen once — before this, the two components carried near-identical
// copies of this logic and a real bug (react-hook-form dropping the verified `code`
// value once the Code step unmounted) had to be found and fixed independently in each.
export const useCustomerSignUp = (
  customer: SignUpCustomerArg | null | undefined,
  { onLoggedOut, onSignedUp }: Args,
) => {
  const { data: currentShopData } = useQuery(GET_CURRENT_SHOP_TWO_FACTOR_AUTH)
  const twoFactorAuth = Boolean(currentShopData?.currentShop?.twoFactorAuth)

  const [state, setState] = useSignUpState(customer?.signedUp ? SignUpStates.COMPLETED : SignUpStates.PHONE)
  const [showDetails, setShowDetails] = useState(false)

  if (!showDetails && (state.isForm || state.isMultiple)) {
    setShowDetails(true)
  }

  const form = useForm<SignUpFormState>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: DEFAULT_FORM_VALUES,
  })
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0)
  const wasCodeStepRef = useRef(false)
  const codeInputRef: RefObject<HTMLInputElement> = useRef(null)

  const { clearErrors, handleSubmit, reset, getValues, setValue } = form

  const startResendCooldown = useEvent(() => setResendSecondsLeft(RESEND_COOLDOWN_SECONDS), [])
  const stopResendCooldown = useEvent(() => setResendSecondsLeft(0), [])

  const [signUp, { loading: signing }] = useMutation(SIGN_UP_CUSTOMER, {
    onCompleted: (data) => {
      if (data.signUpCustomer.state == SignUpStates.COMPLETED) {
        const signedUpCustomer = data.signUpCustomer.customer
        onSignedUp(pick(signedUpCustomer, ['firstName', 'lastName', 'email', 'phone']))
        sendGTMAccountCreatedEvent(signedUpCustomer as Customer)
      } else if (data.signUpCustomer.state == SignUpStates.CODE) {
        noty('Enter code from SMS', 'success')

        if (!wasCodeStepRef.current) {
          wasCodeStepRef.current = true
          startResendCooldown()
          window.requestAnimationFrame(() => {
            codeInputRef.current?.focus()
          })
        }
      }
      setState(data.signUpCustomer.state as SignUpStates)
    },
  })
  const [logout] = useMutation(LOGOUT_CUSTOMER)

  const handleLogout = useEvent(
    () =>
      void logout().then(() => {
        onLoggedOut?.()
        setState(SignUpStates.PHONE)
        setResendSecondsLeft(0)
        setShowDetails(false)
        reset(DEFAULT_FORM_VALUES)
        wasCodeStepRef.current = false
      }),
    [logout, onLoggedOut],
  )

  const onSubmit = handleSubmit((input: SignUpFormState) => {
    if (customer?.signedUp) {
      onSignedUp(pick(customer, ['firstName', 'lastName', 'email', 'phone']))
    } else {
      void signUp({ variables: { input } })
    }
  })

  const handleResend = useEvent(() => {
    if (resendSecondsLeft > 0) return
    startResendCooldown()
    clearErrors('code')
    setValue('code', '')
    codeInputRef.current?.focus()
    void signUp({ variables: { input: getValues() } })
  })

  return {
    codeInputRef,
    form,
    handleLogout,
    handleResend,
    onSubmit,
    resendSecondsLeft,
    showDetails,
    signing,
    state,
    stopResendCooldown,
    twoFactorAuth,
  }
}
